import { designSystemColorPalettes, type SharedUiColorNameV2 } from '@/shared/ui/theming';
import { fillColorPaletteVariant } from '@/ui/chart/charts.constants';
import { type IconName, UiIconComponent } from '@/ui/icon/ui-icon';
import { arraysContainSameElements } from '@/utils/toolkit/array/arrays-contain-same-elements.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { LowerCasePipe, NgClass } from '@angular/common';
import { Component, type ElementRef, inject, Input, NgZone, type SimpleChanges, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import type { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { DeveloperHelperComponent } from '../../../../../components/developer-helper/developer-helper';
import { LoadingAndErrorComponent } from '../../../../../components/loading-and-error/loading-and-error';
import { WidgetCardComponent } from '../../../../../components/widgets/widget-card/widget-card';
import { SharedUiColoredTextColorDirective } from '../../../../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { HighchartsStaticProvider } from '../../../../../providers/highcharts/highcharts.static.provider';
import { OldDataSourceCode } from '../../../../../services/developer-helper.service';

export interface PieChartDataPoint {
    label: string;
    shortLabel: string;
    color: SharedUiColorNameV2;
    icon?: IconName;
    value: number;
}

export interface PieChartDataSource {
    cardIcon: string;
    cardTitle: string;

    headerButtonText: string;
    headerButtonClick: Function;

    noDataTitle: string;
    noDataSubtitle?: string;
    noDataButtonText?: string;
    noDataButtonClick?: Function;

    loadingTitle: string;
    loadingSubtitle?: string;
    helpButtonUrl?: string;

    data$: Observable<PieChartDataPoint[]>;
}

/**
 * @DEPRECATED
 * ONLY USE IN widget-actions-state. Do not use it again please....
 */
@UntilDestroy()
@Component({
    styleUrls: ['./widget-pie-chart.scss'],
    selector: 'widget-pie-chart',
    templateUrl: 'widget-pie-chart.html',
    imports: [
        WidgetCardComponent,
        DeveloperHelperComponent,
        NgClass,
        SharedUiColoredTextColorDirective,
        UiIconComponent,
        LoadingAndErrorComponent,
        LowerCasePipe,
        TranslatePipe
    ]
})
export class WidgetPieChartComponent {
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private translateService = inject(TranslateService);
    private _zone = inject(NgZone);

    mode: 'pie' | 'loading' | 'nodata' = 'loading';

    private _dataSourceSubscription: Subscription | null = null;
    @Input() dataSource!: PieChartDataSource;

    @ViewChild('chartCanvas')
    private canvas!: ElementRef;

    data: PieChartDataPoint[] = [];
    private highChartsObject: any = null;

    OldDataSourceCode = OldDataSourceCode;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.dataSource) {
            if (this._dataSourceSubscription) {
                this._dataSourceSubscription.unsubscribe();
                this._dataSourceSubscription = null;
            }
            if (this.dataSource && this.dataSource.data$) {
                this._dataSourceSubscription = this.dataSource.data$
                    .pipe(distinctUntilChanged(arraysContainSameElements), filter(isNotNullish), untilDestroyed(this))
                    .subscribe(data => {
                        this.setData(data);
                    });
            }
        }
    }

    ngAfterViewInit() {}

    private buildChart() {
        if (!this.highChartsObject) {
            const options = this._highchartsStaticProvider.buildConfig(
                this._highchartsStaticProvider.DISABLE_ANIMATIONS,
                this._highchartsStaticProvider.REFLOW_ON_CONTAINER_RESIZE,
                this.getChartConfig()
            );
            this.highChartsObject = Highcharts.chart(this.canvas.nativeElement, options as any);
        }

        const data: any[] = [];
        const percentageStep = this.data.reduce((c, p) => c + p.value, 0);

        for (const dataPoint of this.data) {
            const dist = (dataPoint.value * 100) / percentageStep;
            if (dataPoint.value >= 1) {
                data.push({
                    name: this.translateService.instant(dataPoint.shortLabel),
                    y: dataPoint.value,
                    color: designSystemColorPalettes[dataPoint.color][fillColorPaletteVariant].solid,
                    dataLabels: {
                        distance: dist < 10 ? 50 : dist < 20 ? 20 : -30
                    },
                    dataPoint
                });
            }
        }
        this.highChartsObject.series[0].setData(data, true);
    }

    private getChartConfig(): any {
        return {
            title: {
                text: '',
                align: 'center',
                margin: 30
            },
            subtitle: {
                text: '',
                align: 'center'
            },
            chart: {
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
                type: 'pie',
                height: 400
            },
            plotOptions: {
                pie: {
                    allowPointSelect: false,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        // distance: -40,
                        format: '{point.name}<br>{point.percentage:.0f}%',
                        style: {
                            textOutline: 'none'
                        }
                    },
                    size: '100%'
                }
            },
            tooltip: {
                headerFormat: '<span>' + this.translateService.instant('PIE_CHARTS.HOME.ACTIONS.NUMBER_OF_ACTIONS') + '</span><br>',
                pointFormat: '<span style="color:{point.color}">{point.name}</span>: <b>{point.y:.0f}{point.uom}</b><br/>'
            },
            series: [
                {
                    name: this.translateService.instant('PIE_CHARTS.HOME.ACTIONS.NUMBER_OF_ACTIONS'),
                    colorByPoint: true,
                    data: [],
                    point: {
                        events: {}
                    }
                }
            ],
            loading: true,
            credits: { enabled: false },
            exporting: { enabled: false }
        };
    }

    private setData(data: PieChartDataPoint[]) {
        this.data = data;
        if (data === null) {
            this.mode = 'loading';
        } else if (data.reduce((sum, p) => sum + p.value, 0) === 0) {
            this.mode = 'nodata';
        } else {
            this.mode = 'pie';
            setTimeout(() => {
                this._zone.runOutsideAngular(() => {
                    this.buildChart();
                });
            }, 10);
        }
    }
}
